/**
 * Account Display Preferences Service — my.collabkare.id
 *
 * DSC-1B.2 client. Mirrors the DSC-1A.2 notification-prefs pattern:
 * reads + writes go through sandman REST (canonical at sandman PG;
 * Firestore is a derived read-only projection). For real-time
 * cross-tab sync the UI ALSO subscribes to the Firestore projection
 * doc — but the subscription is a READ. NEVER write to Firestore
 * from here.
 *
 * Architecture per docs/architecture/data-storage-consolidation-plan.md v3:
 *   - PUT /api/v1/me/account-display-prefs (sandman) → writes
 *     account_display_prefs row → sandman publishes projection to
 *     Firestore accountDisplayPrefs/{userId}
 *   - GET /api/v1/me/account-display-prefs (sandman) → returns the
 *     canonical row (used for first paint + as a fallback when the
 *     listener hasn't ticked yet)
 *   - Firestore onSnapshot(accountDisplayPrefs/{userId}) → real-time
 *     push on subsequent updates (e.g. updates from another tab/device)
 */

import { apiGet, apiPut } from "@/lib/api-client";

const SANDMAN_URL =
  import.meta.env.VITE_SANDMAN_API_URL || "https://sandman.collabkare.io";

// NestJS wraps responses in { statusCode, message, data } — same shape
// as billing-service and notification-prefs-service.
interface NestEnvelope<T> {
  statusCode: number;
  message: string;
  data: T;
}

function unwrap<T>(response: T | NestEnvelope<T>): T {
  if (
    response &&
    typeof response === "object" &&
    "data" in response &&
    "statusCode" in response
  ) {
    return (response as NestEnvelope<T>).data;
  }
  return response as T;
}

export type ThemePreference = "light" | "dark" | "system";

/**
 * Shape returned by sandman GET / sent on sandman PUT.
 * Matches the sandman AccountDisplayPrefsDto exactly.
 */
export interface AccountDisplayPrefs {
  theme: ThemePreference;
  defaultLandingRoute: string;       // must start with `/`
  dashboardWidgetOrder: string[];    // empty = catalog default order
}

/** What sandman includes in the read response in addition to the DTO. */
export interface AccountDisplayPrefsRow extends AccountDisplayPrefs {
  id: string;
  userId: string;
  updatedAt: string;  // ISO
  createdAt: string;
}

/**
 * GET caller's prefs. Returns null when the user hasn't saved any yet
 * (sandman returns null body in that case; the UI shows catalog defaults).
 */
export async function getMyAccountDisplayPrefs(): Promise<AccountDisplayPrefsRow | null> {
  const res = await apiGet<AccountDisplayPrefsRow | null | NestEnvelope<AccountDisplayPrefsRow | null>>(
    `${SANDMAN_URL}/api/v1/me/account-display-prefs`,
  );
  const unwrapped = unwrap(res);
  return unwrapped ?? null;
}

/**
 * PUT caller's prefs. Sandman upserts + writes audit row + projects to
 * Firestore on success.
 */
export async function putMyAccountDisplayPrefs(
  input: AccountDisplayPrefs,
): Promise<AccountDisplayPrefsRow> {
  const res = await apiPut<AccountDisplayPrefsRow | NestEnvelope<AccountDisplayPrefsRow>>(
    `${SANDMAN_URL}/api/v1/me/account-display-prefs`,
    input,
  );
  return unwrap(res);
}

/**
 * Catalog of dashboard widgets the portal currently knows how to
 * render. The UI surfaces these in the reorder list; user-saved IDs
 * not in the catalog are ignored at render time. New widgets get
 * added here as they ship — no DB migration needed.
 */
export const DASHBOARD_WIDGET_CATALOG: Array<{
  id: string;
  label: string;
}> = [
  { id: "balance", label: "Credit Balance" },
  { id: "recentActivity", label: "Recent Activity" },
  { id: "verification", label: "Verification Status" },
  { id: "credits", label: "Credit History" },
  { id: "orders", label: "Recent Orders" },
  { id: "notifications", label: "Notifications" },
  { id: "subscriptions", label: "Subscriptions" },
  { id: "support", label: "Support Tickets" },
];

/**
 * Default values for first-time setup. Match the entity's column
 * defaults in sandman so what the UI shows pre-save is what sandman
 * would persist on first PUT.
 */
export const DEFAULT_ACCOUNT_DISPLAY_PREFS: AccountDisplayPrefs = {
  theme: "system",
  defaultLandingRoute: "/dashboard",
  dashboardWidgetOrder: [],
};
